import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Activity, Timer, ArrowRight } from "lucide-react";

const STATUS_DOT = {
  online: "bg-emerald-500",
  idle: "bg-amber-400",
  offline: "bg-muted-foreground/40",
};

export const TeamPulseWidget = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    if (!user?.operations_director) return;
    let alive = true;
    const load = () => API.get("/monitoring/team")
      .then(r => { if (alive) setTeam(r.data.team || []); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 45000);
    return () => { alive = false; clearInterval(iv); };
  }, [user?.operations_director]);

  if (!user?.operations_director || team === null) return null;

  const counts = team.reduce((a, m) => { a[m.status] = (a[m.status] || 0) + 1; return a; }, {});
  const visible = team.filter(m => m.status !== "offline").slice(0, 6);

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-4" data-testid="team-pulse-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-emerald-600" /> Team Pulse
          </h3>
          <span className="text-xs text-muted-foreground" data-testid="team-pulse-counts">
            {counts.online || 0} online · {counts.idle || 0} idle · {counts.offline || 0} offline
          </span>
        </div>
        <Link to="/staff-monitoring" className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1" data-testid="team-pulse-open-link">
          Open Staff Monitoring <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {visible.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3" data-testid="team-pulse-empty">No one is online right now.</p>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(m => (
            <div key={m.user_id} className="flex items-center gap-2.5 rounded-md border border-border/60 bg-background px-3 py-2" data-testid={`team-pulse-member-${m.user_id}`}>
              <span className={`h-2 w-2 rounded-full shrink-0 ${STATUS_DOT[m.status]}`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{m.name}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {m.status === "idle" ? "Idle" : (m.current_section || "Online")} · {m.active_minutes}m active today
                </p>
              </div>
              {m.running_timer && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-medium shrink-0" title={m.running_timer.label || m.running_timer.task_title || m.running_timer.project_name || "Timer running"}>
                  <Timer className="h-3 w-3" /> {(m.running_timer.label || m.running_timer.task_title || m.running_timer.project_name || "Timing").slice(0, 18)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamPulseWidget;
